// ---------------------------------------------------------------------------
// The background-list editor's state machine: delete, reorder, append an
// upload, undo, redo.
//
// Pure and I/O free. The hook around it (src/hooks/useBgListEditor.ts) is what
// writes the React Query cache and PUTs the result; everything about WHAT the
// next list looks like — including which deletions need a tombstone and how far
// back undo reaches — is decided here so it can be tested directly.
//
// Undo/redo is client-side only (plan D5). The server stores one arrangement
// per (admin, channel, type), never a history: an undo is just another write.
// ---------------------------------------------------------------------------

/** How many steps back (and forward) the editor remembers. */
export const HISTORY_LIMIT = 20;

/** The fields the editor needs from a resolved list entry. */
export interface BgListEntryLike {
  entryKey: string;
  kind: "catalog" | "upload";
  sourceVariation: number | null;
  /** Tile number, rewritten on every change so it is always `index + 1`. */
  position: number;
  /** Identity (src/libs/bgIdentity.ts). Never changes on a reorder. */
  variation: number;
  [field: string]: unknown;
}

/** One editable state of the list. */
export interface BgListSnapshot {
  /** The arrangement, in display order. */
  list: BgListEntryLike[];
  /** Tombstones — entryKeys of PUBLISHED entries the admin deleted. */
  removed: string[];
}

export interface BgListEditorState {
  past: BgListSnapshot[];
  present: BgListSnapshot;
  future: BgListSnapshot[];
}

export type BgListEditorAction =
  | {type: "reorder"; fromKey: string; toKey: string}
  | {type: "remove"; entryKey: string}
  | {type: "add"; entry: BgListEntryLike}
  | {type: "undo"}
  | {type: "redo"}
  | {type: "sync"; list: BgListEntryLike[]; removed: string[]};

/** Rewrite `position` so it equals the entry's place in the list. The
 * `variation` (the identity that stored data addresses) stays the same. */
function renumber(list: BgListEntryLike[]): BgListEntryLike[] {
  return list.map((entry, index) =>
    entry.position === index + 1 ? entry : {...entry, position: index + 1}
  );
}

/** A fresh editor over a server-resolved list. History starts empty. */
export function initBgListEditor(
  list: BgListEntryLike[],
  removed: string[] = []
): BgListEditorState {
  return {past: [], present: {list: renumber(list), removed}, future: []};
}

/** Push a new present, dropping the oldest step past the cap and killing redo. */
function commit(state: BgListEditorState, next: BgListSnapshot): BgListEditorState {
  const past = [...state.past, state.present].slice(-HISTORY_LIMIT);
  return {past, present: next, future: []};
}

export function reduceBgListEditor(
  state: BgListEditorState,
  action: BgListEditorAction
): BgListEditorState {
  switch (action.type) {
    case "reorder": {
      const {list, removed} = state.present;
      const from = list.findIndex((entry) => entry.entryKey === action.fromKey);
      const to = list.findIndex((entry) => entry.entryKey === action.toKey);
      if (from === -1 || to === -1 || from === to) return state;
      const next = [...list];
      next.splice(to, 0, ...next.splice(from, 1));
      return commit(state, {list: renumber(next), removed});
    }

    case "remove": {
      const {list, removed} = state.present;
      const target = list.find((entry) => entry.entryKey === action.entryKey);
      if (!target) return state;
      const next = list.filter((entry) => entry.entryKey !== action.entryKey);
      // Only a PUBLISHED entry needs a tombstone. A catalog entry is published
      // by definition (it comes from the catalog, promoted uploads included),
      // so without one it would be appended straight back at the tail on the
      // next resolve. A draft upload is only ever in this admin's own list, so
      // dropping it is enough.
      const nextRemoved =
        target.kind === "catalog" && !removed.includes(action.entryKey)
          ? [...removed, action.entryKey]
          : removed;
      return commit(state, {list: renumber(next), removed: nextRemoved});
    }

    case "add": {
      const {list, removed} = state.present;
      if (list.some((entry) => entry.entryKey === action.entry.entryKey)) return state;
      // An upload joins as the LAST tile, and un-tombstones itself in case the
      // same image was deleted earlier in this session.
      return commit(state, {
        list: renumber([...list, action.entry]),
        removed: removed.filter((key) => key !== action.entry.entryKey),
      });
    }

    case "undo": {
      if (state.past.length === 0) return state;
      const previous = state.past[state.past.length - 1];
      return {
        past: state.past.slice(0, -1),
        present: previous,
        future: [state.present, ...state.future].slice(0, HISTORY_LIMIT),
      };
    }

    case "redo": {
      if (state.future.length === 0) return state;
      const [next, ...rest] = state.future;
      return {
        past: [...state.past, state.present].slice(-HISTORY_LIMIT),
        present: next,
        future: rest,
      };
    }

    case "sync":
      // A new server list (a product switch, a publish, a reset) replaces the
      // whole editor: the old history describes a list that no longer exists —
      // and so do the old TOMBSTONES. The server's effective `removed` for the
      // new scope comes with the list; carrying the previous state's over is
      // how a deletion made on one product used to leak into the next
      // product's draft on its first write.
      return initBgListEditor(action.list, action.removed);

    default:
      throw new Error(`Unknown bg-list editor action: ${JSON.stringify(action)}`);
  }
}

/** The write payload for PUT /api/v3/bg-list. */
export function toBgListWriteEntries(
  snapshot: BgListSnapshot
): Array<{kind: "catalog"; variation: number} | {kind: "upload"; uploadId: string}> {
  return snapshot.list.map((entry) => {
    if (entry.kind === "catalog") {
      if (typeof entry.sourceVariation !== "number") {
        throw new Error(`Catalog entry ${entry.entryKey} has no sourceVariation`);
      }
      return {kind: "catalog", variation: entry.sourceVariation};
    }
    return {kind: "upload", uploadId: entry.entryKey.slice("upload:".length)};
  });
}

/** True when the list has an entry with this identity. */
export function hasVariation(list: BgListEntryLike[], variation: number): boolean {
  return list.some((entry) => entry.variation === variation);
}
